import ContentContainer from '@/components/containers/contentContainer';
import CustomButton from '@/shared/button/customButton';
import { api } from '@/utils/api';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Popup from '@/shared/popups/popupDetails';
import { Divider, IconButton, Pagination, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { toast } from "@/utils/appToast";
import { useNavigate } from 'react-router-dom';
import { FilterI } from '@/types/interfaces';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/shared/spinner/spinner';
import TextArea from '@/shared/textArea/textArea';
import JobSkeleton from '@/utils/jobskeleton'

const DisputesList = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: { user }) => state.user);
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<any>({});
  const [fetchingDisputes, setFetchingDisputes] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<any>(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [offset, setOffset] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [resolutionNotes, setResolutionNotes] = useState<string>('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const hasPageBeenRendered = useRef(false);
  const [filterValue, setFilterValue] = useState<FilterI>({
    text: '',
    page: offset,
    limit: pageSize,
    order: 'desc',
    clientName: '',
    workerName: '',
    status: statusFilter
  });

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { text: string; color: string; dot: string }> = {
      'OPEN': { text: t('open'), color: 'text-red-500', dot: 'bg-red-500' },
      'UNDER_REVIEW': { text: t('underReview'), color: 'text-yellow-500', dot: 'bg-yellow-500' },
      'Resolve_&_refund_payment': { text: t('Resolve_&_refund_payment'), color: 'text-green-500', dot: 'bg-green-500' },
      'Resolve_&_Release_Payment': { text: t('Resolve_&_Release_Payment'), color: 'text-green-500', dot: 'bg-green-500' },
      'CLOSED': { text: t('closed'), color: 'text-gray-500', dot: 'bg-gray-500' }
    };
    return statusMap[status] || { text: status, color: 'text-gray-500', dot: 'bg-gray-500' };
  };

  const fetchDisputes = async () => {
    try {
      setFetchingDisputes(true);
      const filters: any = {
        page: offset,
        limit: pageSize
      };
      if (statusFilter) {
        filters.status = statusFilter;
      }

      const res = user?.type === 'ADMIN' 
        ? await api.getAdminDisputes(filters)
        : await api.getDisputes(filters);

      if (res && res.success) {
        setDisputes(res.data || {});
        hasPageBeenRendered.current = true;
      }
    } catch (err) {
      console.error("Failed to fetch disputes", err);
      toast.error('Error al cargar las disputas');
    } finally {
      setFetchingDisputes(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  useEffect(() => {
    if (hasPageBeenRendered.current === true) {
      const delayDebounceFn = setTimeout(() => {
        fetchDisputes();
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterValue, statusFilter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setOffset(newPage);
    setFilterValue({ ...filterValue, page: newPage });
  };

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setOffset(1);
    setFilterValue({ ...filterValue, page: 1, status: newStatus });
  };

  const handleViewDispute = async (disputeId: string) => {
    try {
      const res = user?.type === 'ADMIN'
        ? await api.getAdminDisputeById(disputeId)
        : await api.getDisputeById(disputeId);
      
      if (res && res.success) {
        setSelectedDispute(res.data);
        setNewStatus(res.data.status);
        setResolutionNotes(res.data.resolutionNotes || '');
        setShowDetailPopup(true);
      } else {
        toast.error('Error al cargar los detalles de la disputa');
      }
    } catch (error) {
      console.error('Error fetching dispute details:', error);
      toast.error('Error al cargar los detalles de la disputa');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedDispute || !newStatus) return;

    setIsUpdatingStatus(true);
    try {
      const res = await api.updateDisputeStatus(selectedDispute._id, {
        status: newStatus,
        resolutionNotes: resolutionNotes.trim() || undefined
      });

      if (res && res.success) {
        toast.success(t('disputeUpdated') || 'Estado de la disputa actualizado exitosamente');
        setShowDetailPopup(false);
        setSelectedDispute(null);
        fetchDisputes();
      } else {
        toast.error(res?.message || 'Error al actualizar el estado de la disputa');
      }
    } catch (error: any) {
      console.error('Error updating dispute status:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error al actualizar el estado de la disputa');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getServiceName = (job: any) => {
    if (typeof job?.service === 'object' && job?.service?.name) return job.service.name;
    if (typeof job?.service === 'string') return job.service;
    return '-';
  };

  const formatJobId = (uid: string | undefined) => {
    if (!uid) return '-';
    return uid.startsWith('JOB-') ? uid.substring(4) : uid;
  };

  return (
    <ContentContainer className='!px-2 m-0 bg-gray-100'>
      <div className='flex flex-col gap-3'>
        <h1 className='text-2xl text-black font-bold'>{t('disputes')}</h1>
        
        {/* Status Filter */}
        <div className="flex gap-4 items-center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('filterByStatus')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              label={t('filterByStatus')}
            >
              <MenuItem value="">{t('all') || 'All'}</MenuItem>
              <MenuItem value="OPEN">{t('open')}</MenuItem>
              <MenuItem value="UNDER_REVIEW">{t('underReview')}</MenuItem>
              <MenuItem value='Resolve_&_refund_payment'>{t('Resolve_&_refund_payment')}</MenuItem>
              <MenuItem value= 'Resolve_&_Release_Payment'>{t('Resolve_&_Release_Payment')}</MenuItem>
              <MenuItem value="CLOSED">{t('closed')}</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-col gap-4">
          {fetchingDisputes ? (
            <div className="flex justify-center items-center py-10">
             <div className="flex flex-col">
                      {[...Array(5)].map((_, i) => (
                        <JobSkeleton key={i} />
                      ))}
                    </div>
            </div>
          ) : disputes?.data?.length > 0 ? (
            disputes.data.map((dispute: any) => {
              const statusConfig = getStatusConfig(dispute.status);
              return (
                <div
                  key={dispute._id}
                  className="bg-white border border-gray-200 rounded-xl px-6 py-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleViewDispute(dispute._id)}
                >
                  <div className="grid lg:grid-cols-5 md:grid-cols-3 grid-cols-2 gap-4 items-center">
                    <div className='flex flex-col gap-2'>
                      <span className='text-sm font-semibold text-black'>{t('jobId')}</span>
                      <span className="truncate max-w-[200px]" title={formatJobId(dispute.job?.uid || dispute.uid)}>
                        {formatJobId(dispute.job?.uid || dispute.uid)}
                      </span>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <span className='text-sm font-semibold text-black'>{t('issueType')}</span>
                      <span>{t(dispute.issueType?.toLowerCase().replace('_', '') || 'other')}</span>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <span className='text-sm font-semibold text-black'>{t('disputeStatus')}</span>
                      <span className={`flex items-center gap-1 font-semibold ${statusConfig.color}`}>
                        <span className={`w-2 h-2 min-w-[8px] min-h-[8px] rounded-full ${statusConfig.dot}`}></span>
                        {statusConfig.text}
                      </span>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <span className='text-sm font-semibold text-black'>{t('createdDate')}</span>
                      <span>{moment(dispute.createdAt).format('DD MMM YYYY')}</span>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <span className='text-sm font-semibold text-black'>{t('createdBy')}</span>
                      <span className="truncate max-w-[150px]">
                        {dispute.createdBy?.first_name && dispute.createdBy?.last_name
                          ? `${dispute.createdBy.first_name} ${dispute.createdBy.last_name}`
                          : dispute.createdBy?.email || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex justify-center items-center h-full py-10">
              <span className="text-gray-500 text-sm">{t('noDisputes')}</span>
            </div>
          )}
        </div>
        {disputes?.pagination?.totalPages > 1 && (
          <div className="flex justify-center">
            <Pagination
              count={disputes?.pagination?.totalPages || 1}
              color="primary"
              variant="outlined"
              page={offset}
              onChange={handleChangePage}
              showFirstButton
              showLastButton
              siblingCount={0}
            />
          </div>
        )}
      </div>

      {/* Dispute Detail Popup */}
      <Popup
        isOpen={showDetailPopup}
        handleClose={() => {
          setShowDetailPopup(false);
          setSelectedDispute(null);
        }}
        className="bg-white"
        size="md"
        headerTitle={t('disputeDetails')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          selectedDispute && (
            <div className="p-4 w-full overflow-y-auto max-h-[80vh]">
              <div className="w-full overflow-x-hidden break-words">
                {/* Details grid */}
                <div className="grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 w-full gap-4 text-sm font-semibold border-b border-gray-200 pb-4 mb-4">
                  <div>
                    <div className="text-sm text-black font-semibold mb-1">{t('jobId')}</div>
                    <div className="font-normal text-black">
                      {formatJobId(selectedDispute.job?.uid || selectedDispute.uid)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-black font-semibold mb-1">{t('issueType')}</div>
                    <div className="font-normal text-black">
                      {t(selectedDispute.issueType?.toLowerCase().replace('_', '') || 'other')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-black font-semibold mb-1">{t('disputeStatus')}</div>
                    <div className={`font-semibold ${getStatusConfig(selectedDispute.status).color}`}>
                      {getStatusConfig(selectedDispute.status).text}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-black font-semibold mb-1">{t('createdDate')}</div>
                    <div className="font-normal text-black">
                      {moment(selectedDispute.createdAt).format('DD MMM YYYY HH:mm')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-black font-semibold mb-1">{t('createdBy')}</div>
                    <div className="font-normal text-black">
                      {selectedDispute.createdBy?.first_name && selectedDispute.createdBy?.last_name
                        ? `${selectedDispute.createdBy.first_name} ${selectedDispute.createdBy.last_name}`
                        : selectedDispute.createdBy?.email || '-'}
                    </div>
                  </div>
                </div>

                <Divider className="border-gray-400 my-4" />

                {/* Description */}
                <div className="mb-4">
                  <div className="font-semibold mb-2">{t('disputeDescription')}:</div>
                  <div className="text-gray-700 text-sm whitespace-pre-wrap break-words p-3 bg-gray-50 rounded-lg">
                    {selectedDispute.description}
                  </div>
                </div>

                {/* Images */}
                {selectedDispute.imageUrls && selectedDispute.imageUrls.length > 0 && (
                  <>
                    <Divider className="border-gray-400 my-4" />
                    <div className="mb-4">
                      <div className="font-semibold mb-2">{t('uploadImages')}:</div>
                      <div className="flex flex-wrap gap-3">
                        {selectedDispute.imageUrls.map((url: string, idx: number) => (
                          <img
                            key={idx}
                            src={url}
                            alt={`Dispute image ${idx + 1}`}
                            className="h-32 w-32 object-cover rounded-md border cursor-pointer hover:scale-105 transition-transform"
                            onClick={() => window.open(url, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Resolution Notes (if resolved) */}
                {selectedDispute.resolutionNotes && user?.type !== 'ADMIN' && (
                  <>
                    <Divider className="border-gray-400 my-4" />
                    <div className="mb-4">
                      <div className="font-semibold mb-2">{t('resolutionNotes')}:</div>
                      <div className="text-gray-700 text-sm whitespace-pre-wrap break-words p-3 bg-blue-50 rounded-lg">
                        {selectedDispute.resolutionNotes}
                      </div>
                    </div>
                  </>
                )}

                {/* Admin Status Update Section */}
                {user?.type === 'ADMIN' && (
                  <>
                    <Divider className="border-gray-400 my-4" />
                    <div className="mb-4">
                      <div className="font-semibold mb-2">{t('updateStatus')}:</div>
                      <FormControl fullWidth className="mb-4">
                        <InputLabel>{t('disputeStatus')}</InputLabel>
                        <Select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          label={t('disputeStatus')}
                        >
                          <MenuItem value="OPEN">{t('open')}</MenuItem>
                          <MenuItem value="UNDER_REVIEW">{t('underReview')}</MenuItem>
                          <MenuItem value="RESOLVED">{t('resolved')}</MenuItem>
                          <MenuItem value="CLOSED">{t('closed')}</MenuItem>
                        </Select>
                      </FormControl>
                      <div className="mb-4">
                        <label className="block text-sm font-semibold mb-2 text-black">
                          {t('resolutionNotes')} ({t('optional') || 'Optional'})
                        </label>
                        <TextArea
                          name="resolutionNotes"
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder={t('resolutionNotes')}
                          className="w-full min-h-[100px] rounded text-base"
                          maxLength={1000}
                        />
                      </div>
                      <div className="flex gap-4 justify-end">
                        <CustomButton
                          label={t('cancel')}
                          className="!rounded-lg px-6 py-3 border bg-white border-gray-300"
                          labelStyle="text-base text-gray-700"
                          handleButtonClick={() => {
                            setShowDetailPopup(false);
                            setSelectedDispute(null);
                            setNewStatus('');
                            setResolutionNotes('');
                          }}
                        />
                        <CustomButton
                          label={t('updateStatus')}
                          className="!rounded-lg px-6 py-3 bg-blue-500"
                          labelStyle="text-base text-white"
                          handleButtonClick={handleUpdateStatus}
                          disabled={isUpdatingStatus || !newStatus || newStatus === selectedDispute.status}
                          isLoading={isUpdatingStatus}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        }
      />
    </ContentContainer>
  );
};

export default DisputesList;

